// ─────────────────────────────────────────────────────────────────────────────
// SETUP-GUIDE CATALOG — front-owned source of truth for the guide's STRUCTURE.
// The backend only stores per-step `completed_at`/`skipped_at` (see SetupStepStateDto);
// everything visual lives here: which levels/groups/steps exist, their order,
// texts, navigation, dependencies, and the two integrity attributes the backend
// also knows (`skippable`, `countsForCompletion`).
//
// Backend catalog sync: every `code` below must exist in the backend catalog for
// skip/state to work. A code the backend doesn't track reads as permanently
// pending and rejects skip — keep this list and the backend catalog in lockstep.
// ─────────────────────────────────────────────────────────────────────────────

import {
  CONDOMINIUM_LIST_PAGE_URL,
  DIGITAL_CERTIFICATE_SETTINGS_PAGE_URL,
  NFE_SETTINGS_PAGE_URL,
  PICKLIST_LIST_PAGE_URL,
  PLANOGRAM_LIST_PAGE_URL,
  POINT_OF_SALE_LIST_PAGE_URL,
  PRODUCT_LIST_PAGE_URL,
  STOCK_CONTROL_MOVEMENT_PAGE_URL,
  SUBSCRIPTION_PAGE_URL,
  TERMINAL_LIST_PAGE_URL,
} from '@/api/core/links';

export type StepActionType = 'REGISTER' | 'ASSOCIATE' | 'CONFIGURE' | 'OPERATION' | 'TEACH';

export type StepCatalogEntry = {
  /** Stable code shared with the backend (status + skip key). */
  code: string;
  title: string;
  description: string;
  actionType: StepActionType;
  /** Whether the user may dismiss this single step. */
  skippable: boolean;
  /** Whether completing this step is required for the whole guide to be "done". */
  countsForCompletion: boolean;
  /** Prerequisite step code — this step stays LOCKED until it is completed. */
  dependsOn?: string;
  /** In-app navigation target for the step CTA (absent ⇒ no navigation). */
  route?: string;
  icon: string;
  /** Render the terminal env-id activation panel inside this step. */
  showEnvPanel?: boolean;
};

export type GroupCatalogEntry = {
  /** Optional group heading shown above its first step. */
  title?: string;
  steps: StepCatalogEntry[];
};

export type LevelCatalogEntry = {
  code: string;
  title: string;
  description: string;
  /** Optional levels do not block the guide from being considered complete. */
  optional: boolean;
  /** Module that must be active for this level to unlock (e.g. fiscal). */
  requiresModule?: string;
  /** Shown when the level is locked by a missing module. */
  lockReason?: string;
  groups: GroupCatalogEntry[];
};

export const TAX_MODULE_CODE = 'TAX_MODULE';

// Where the "unlock the fiscal module" CTA points when a level is module-locked
// (the subscription details page lists modules with a "Contratar" button).
export const MODULES_UPSELL_ROUTE = SUBSCRIPTION_PAGE_URL;

const CTA_LABELS: Record<StepActionType, string> = {
  REGISTER: 'Cadastrar',
  ASSOCIATE: 'Ativar',
  CONFIGURE: 'Configurar',
  OPERATION: 'Criar',
  TEACH: 'Aprender',
};
export const ctaLabelFor = (action: StepActionType): string => CTA_LABELS[action];

export const SETUP_GUIDE_CATALOG: LevelCatalogEntry[] = [
  {
    code: 'START_SELLING',
    title: 'Comece a vender',
    description: 'Do cadastro ao terminal vendendo.',
    optional: false,
    groups: [
      {
        steps: [
          {
            code: 'PRODUCT_REGISTRATION',
            title: 'Cadastrar produtos',
            description: 'O catálogo que será vendido.',
            actionType: 'REGISTER',
            skippable: false,
            countsForCompletion: true,
            route: PRODUCT_LIST_PAGE_URL,
            icon: 'ki-box',
          },
          {
            code: 'COMMUNITY_REGISTRATION',
            title: 'Cadastrar condomínio',
            description: 'Onde o mercado fica instalado.',
            actionType: 'REGISTER',
            skippable: false,
            countsForCompletion: true,
            route: CONDOMINIUM_LIST_PAGE_URL,
            icon: 'ki-home-2',
          },
          {
            code: 'POINT_OF_SALE_REGISTRATION',
            title: 'Cadastrar ponto de venda',
            description: 'O mercadinho dentro do condomínio.',
            actionType: 'REGISTER',
            skippable: false,
            countsForCompletion: true,
            dependsOn: 'COMMUNITY_REGISTRATION',
            route: POINT_OF_SALE_LIST_PAGE_URL,
            icon: 'ki-shop',
          },
        ],
      },
      {
        title: 'Ativar e vincular terminal',
        steps: [
          {
            code: 'TERMINAL_ACTIVATION',
            title: 'Ativar terminal',
            description: 'Ative o terminal com o ID do ambiente.',
            actionType: 'ASSOCIATE',
            skippable: false,
            countsForCompletion: true,
            dependsOn: 'POINT_OF_SALE_REGISTRATION',
            route: POINT_OF_SALE_LIST_PAGE_URL,
            icon: 'ki-tablet',
            showEnvPanel: true,
          },
          {
            code: 'TERMINAL_POS_ASSOCIATION',
            title: 'Vincular terminal ao PDV',
            description: 'Abra o PDV e selecione o terminal ativado no campo "Terminal".',
            actionType: 'ASSOCIATE',
            skippable: false,
            countsForCompletion: true,
            dependsOn: 'TERMINAL_ACTIVATION',
            route: POINT_OF_SALE_LIST_PAGE_URL,
            icon: 'ki-tablet-ok',
          },
        ],
      },
      {
        steps: [
          {
            code: 'PLANOGRAM_REGISTRATION',
            title: 'Montar e ativar planograma',
            description: 'Selecione os produtos à venda no terminal.',
            actionType: 'REGISTER',
            skippable: true,
            countsForCompletion: false,
            dependsOn: 'TERMINAL_POS_ASSOCIATION',
            route: PLANOGRAM_LIST_PAGE_URL,
            icon: 'ki-grid-frame',
          },
        ],
      },
    ],
  },
  {
    code: 'FISCAL',
    title: 'Emita notas fiscais',
    description: 'Fique em dia com a emissão de NFC-e.',
    optional: false,
    requiresModule: TAX_MODULE_CODE,
    lockReason: 'Disponível no módulo Fiscal da sua conta.',
    groups: [
      {
        steps: [
          {
            code: 'FISCAL_CERTIFICATE_UPLOAD',
            title: 'Enviar certificado digital (A1)',
            description: 'Necessário para emitir NFC-e.',
            actionType: 'CONFIGURE',
            skippable: false,
            countsForCompletion: false,
            route: DIGITAL_CERTIFICATE_SETTINGS_PAGE_URL,
            icon: 'ki-shield-tick',
          },
          {
            code: 'NFCE_CONFIGURATION',
            title: 'Configurar NFC-e',
            description: 'CSC, série e ambiente.',
            actionType: 'CONFIGURE',
            skippable: false,
            countsForCompletion: false,
            dependsOn: 'FISCAL_CERTIFICATE_UPLOAD',
            route: NFE_SETTINGS_PAGE_URL,
            icon: 'ki-document',
          },
          {
            code: 'POS_NFCE_ENABLE',
            title: 'Habilitar emissão no ponto de venda',
            description: 'Ative "Emitir cupom fiscal" no PDV.',
            actionType: 'CONFIGURE',
            skippable: false,
            countsForCompletion: false,
            dependsOn: 'NFCE_CONFIGURATION',
            route: POINT_OF_SALE_LIST_PAGE_URL,
            icon: 'ki-check-circle',
          },
        ],
      },
    ],
  },
  {
    code: 'INVENTORY',
    title: 'Gestão de estoque & abastecimento',
    description: 'Mantenha a despensinha abastecida.',
    optional: true,
    groups: [
      {
        steps: [
          {
            code: 'PICKLIST_TASK_GENERATION',
            title: 'Criar uma picklist',
            description: 'Lista de separação dos produtos a abastecer.',
            actionType: 'OPERATION',
            skippable: true,
            countsForCompletion: false,
            route: PICKLIST_LIST_PAGE_URL,
            icon: 'ki-clipboard',
          },
          {
            code: 'SEPARATION_TASK_GENERATION',
            title: 'Gerar tarefa de separação',
            description: 'Separe os produtos da picklist para abastecer.',
            actionType: 'OPERATION',
            skippable: true,
            countsForCompletion: false,
            dependsOn: 'PICKLIST_TASK_GENERATION',
            route: PICKLIST_LIST_PAGE_URL,
            icon: 'ki-parcel',
          },
          {
            code: 'SUPPLY_TASK_GENERATION',
            title: 'Gerar tarefa de abastecimento',
            description: 'Reabasteça o terminal com os produtos separados.',
            actionType: 'OPERATION',
            skippable: true,
            countsForCompletion: false,
            dependsOn: 'PICKLIST_TASK_GENERATION',
            route: PICKLIST_LIST_PAGE_URL,
            icon: 'ki-truck',
          },
          {
            code: 'INVENTORY_REGISTRATION',
            title: 'Movimentar estoque (entrada/saída/balanço)',
            description: 'Lance entradas, saídas ou ajustes de saldo.',
            actionType: 'OPERATION',
            skippable: true,
            countsForCompletion: false,
            route: STOCK_CONTROL_MOVEMENT_PAGE_URL,
            icon: 'ki-arrow-up-down',
          },
        ],
      },
    ],
  },
];
